import React, { useRef, useEffect, useState } from "react";

export function BackgroundBlobs() {
  const canvasRef = useRef(null);
  const mousePos = useRef({ x: 0, y: 0 });
  const smoothMouse = useRef({ x: 0, y: 0 });
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let frameCount = 0;
    let animId = 0;

    const waves = [
      { offset: 0, amplitude: 70, frequency: 0.003, color: "rgba(75, 115, 255, 0.8)", opacity: 0.45 },
      { offset: Math.PI / 2, amplitude: 90, frequency: 0.0026, color: "rgba(0, 23, 255, 0.7)", opacity: 0.35 },
      { offset: Math.PI, amplitude: 60, frequency: 0.0034, color: "rgba(75, 115, 255, 0.65)", opacity: 0.3 },
      { offset: 1.5 * Math.PI, amplitude: 80, frequency: 0.0022, color: "rgba(100, 150, 255, 0.25)", opacity: 0.25 },
      { offset: 2 * Math.PI, amplitude: 55, frequency: 0.004, color: "rgba(150, 180, 255, 0.2)", opacity: 0.2 },
    ];

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const mouseInfluence = reducedMotion ? 10 : 70;
    const mouseRadius = reducedMotion ? 160 : 320;
    const smoothing = reducedMotion ? 0.04 : 0.1;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const drawBackground = () => {
      const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
      grad.addColorStop(0, "#05081D");
      grad.addColorStop(1, "#0a0f2e");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalAlpha = 1;
      ctx.shadowBlur = 0;
    };

    const drawWave = (wave) => {
      ctx.save();
      ctx.beginPath();
      for (let x = 0; x <= canvas.width; x += 4) {
        const dx = x - smoothMouse.current.x;
        const dy = canvas.height / 2 - smoothMouse.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const mouseEffect = Math.max(0, 1 - dist / mouseRadius) * mouseInfluence * Math.sin(0.001 * frameCount + 0.01 * x + wave.offset);

        const y =
          canvas.height / 2 +
          Math.sin(x * wave.frequency + 0.002 * frameCount + wave.offset) * wave.amplitude +
          Math.sin(x * wave.frequency * 0.4 + 0.003 * frameCount) * (0.45 * wave.amplitude) +
          mouseEffect;

        if (x === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
      ctx.lineWidth = 2.5;
      ctx.strokeStyle = wave.color;
      ctx.globalAlpha = wave.opacity;
      ctx.shadowBlur = 35;
      ctx.shadowColor = wave.color;
      ctx.stroke();
      ctx.restore();
    };

    const animate = () => {
      frameCount += 1;
      smoothMouse.current.x += (mousePos.current.x - smoothMouse.current.x) * smoothing;
      smoothMouse.current.y += (mousePos.current.y - smoothMouse.current.y) * smoothing;
      drawBackground();
      waves.forEach(drawWave);
      animId = window.requestAnimationFrame(animate);
    };

    const onMouseMove = (e) => {
      mousePos.current = { x: e.clientX, y: e.clientY };
    };

    const onMouseLeave = () => {
      mousePos.current = { x: canvas.width / 2, y: canvas.height / 2 };
    };

    resize();
    mousePos.current = { x: canvas.width / 2, y: canvas.height / 2 };
    smoothMouse.current = { x: canvas.width / 2, y: canvas.height / 2 };

    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseleave", onMouseLeave);

    drawBackground();
    if (enabled) animId = window.requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseleave", onMouseLeave);
      if (animId) cancelAnimationFrame(animId);
    };
  }, [enabled]);

  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" aria-hidden="true" />

      {/* Floating gradient blobs with animation */}
      <div className="absolute top-[10%] left-1/2 -translate-x-1/2 h-[700px] w-[950px] rounded-full bg-gradient-to-r from-pink-500/[0.18] via-purple-500/[0.12] to-[#0017ff]/[0.18] blur-[160px] opacity-75 animate-[float_8s_ease-in-out_infinite]" />
      <div className="absolute left-[-10%] top-0 h-[520px] w-[520px] rounded-full bg-[#0017ff]/[0.06] blur-[140px] animate-[float_12s_ease-in-out_infinite_2s]" />
      <div className="absolute left-[-15%] top-[40%] h-[450px] w-[450px] rounded-full bg-pink-500/[0.07] blur-[140px] animate-[float_10s_ease-in-out_infinite_4s]" />
      <div className="absolute bottom-0 right-[-10%] h-[500px] w-[500px] rounded-full bg-[#4B73FF]/[0.06] blur-[130px] animate-[float_14s_ease-in-out_infinite_1s]" />

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          25% { transform: translateY(-20px) translateX(10px); }
          50% { transform: translateY(-10px) translateX(-15px); }
          75% { transform: translateY(-25px) translateX(5px); }
        }
      `}</style>
    </div>
  );
}
